import React, { createContext, useState, useReducer, useMemo } from "react";
import { GroupSeries, RiskReport as Report, Query, stringifyQuery, groupSeriesFor, riskBarSeriesFor, RiskSeries } from "../lib/api/charts";
import { Loading } from "./base";

type GroupSeriesSet = { series: RiskSeries; loading: Loading; measure?: any };

export type RiskBarChartContextState = {
  series: {
    [index: string]: GroupSeriesSet;
  };
  getGroupBarSeries(report: Report, query: Query, measure?: any): Partial<GroupSeriesSet>;
};

export const RiskBarChartContext = createContext<RiskBarChartContextState>({
  series: {},
  getGroupBarSeries: () => ({}),
});

type QueryAction = {
  type: "PATCH_SERIES";
  id: string;
  data: Partial<GroupSeriesSet>;
};

const defaultGroupSeries: GroupSeriesSet = {
  series: {
    name: "",
    rows: [],
    scatterdata: { "No Risk": [], "Early Warning": [], Warning: [], Risk: [] },
    classification: "income",
    type: "currency",
    dealers: [],
    xformats: { pos: "", neg: "" },
    yformats: { pos: "", neg: "" },
  },
  loading: { loaded: false, loading: true, error: null },
  measure: {},
};

export const RiskBarChartProvider = ({ children }) => {
  const memo = (report: Report, query: Query, measure?: any) => `${report}-${stringifyQuery(query)}-${JSON.stringify(measure)}`;

  const [series, dispatchGroupSeries] = useReducer((state: { [index: string]: GroupSeriesSet }, action: QueryAction) => {
    switch (action.type) {
      case "PATCH_SERIES":
        return {
          ...state,
          [action.id]: {
            ...state[action.id],
            ...action.data,
          },
        };
      default:
        return state;
    }
  }, {});

  const value = useMemo(
    () => ({
      series,
      getGroupBarSeries: (report: Report, query: Query, measure?: any) => {
        const id = memo(report, query, measure);
        const set = series[id];

        if (set) {
          return set;
        }

        dispatchGroupSeries({
          type: "PATCH_SERIES",
          id,
          data: defaultGroupSeries,
        });

        riskBarSeriesFor(query, report, measure)
          .then(series => {
            dispatchGroupSeries({
              type: "PATCH_SERIES",
              id,
              data: {
                series: series,
                measure,
                loading: {
                  loaded: true,
                  loading: false,
                  error: null,
                },
              },
            });
          })
          .catch(e => {
            dispatchGroupSeries({
              type: "PATCH_SERIES",
              id,
              data: {
                loading: {
                  loaded: false,
                  loading: false,
                  error: e,
                },
              },
            });
          });

        return defaultGroupSeries;
      },
    }),
    [series],
  );

  return <RiskBarChartContext.Provider value={value}>{children}</RiskBarChartContext.Provider>;
};
